import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listDrafts, deleteDraft, DraftSummary } from "../api/drafts";
import { listMySubmissions, MySubmissionSummary } from "../api/mySubmissions";

const MY_SUBMISSIONS_PREVIEW_COUNT = 5;

export default function DraftsPanel({ currentDraftId, onRefreshKey }: { currentDraftId: string | null; onRefreshKey: number }) {
  const [drafts, setDrafts] = useState<DraftSummary[]>([]);
  const [confirmState, setConfirmState] = useState<{ id: string; x: number; y: number } | null>(null);
  const [mySubmissions, setMySubmissions] = useState<MySubmissionSummary[]>([]);

  const reload = () => {
    listDrafts().then(setDrafts).catch(() => setDrafts([]));
    listMySubmissions().then(setMySubmissions).catch(() => setMySubmissions([]));
  };

  useEffect(() => { reload(); }, [onRefreshKey]);

  useEffect(() => {
    if (!confirmState) return;
    function close(e: MouseEvent) {
      const el = e.target as HTMLElement;
      if (el.closest(".delete-confirm-popover")) return;
      setConfirmState(null);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setConfirmState(null);
    }
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [confirmState]);

  const requestDelete = (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const margin = 8;
    const popW = 240;
    const popH = 110;
    const x = Math.min(e.clientX + margin, window.innerWidth - popW - margin);
    const y = Math.min(e.clientY + margin, window.innerHeight - popH - margin);
    setConfirmState({ id, x, y });
  };

  const confirmDelete = async () => {
    if (!confirmState) return;
    const id = confirmState.id;
    setConfirmState(null);
    await deleteDraft(id);
    reload();
  };

  return (
    <aside className="drafts-panel">
      <h3>Saved drafts</h3>
      {drafts.length === 0 && <p className="hint">No drafts yet. Changes auto-save as you type.</p>}
      <ul>
        {drafts.map((d) => {
          const label = (d.projectName || "(untitled)") + (d.jobNumber ? ` — ${d.jobNumber}` : "");
          const isCurrent = d.id === currentDraftId;
          return (
            <li key={d.id} className={isCurrent ? "current" : ""}>
              <Link to={`/?draft=${d.id}`} className="draft-link">
                <div className="draft-title">{label}</div>
                <div className="draft-date">{new Date(d.updatedAt).toLocaleString()}</div>
              </Link>
              <button
                className="link-btn"
                title="Delete draft"
                onClick={(e) => requestDelete(d.id, e)}
              >
                ×
              </button>
            </li>
          );
        })}
      </ul>
      <Link to="/" className="new-link">+ New project</Link>

      <div className="my-submissions-box">
        <h3>
          My submissions
          <Link to="/submissions" className="view-all-link">View all →</Link>
        </h3>
        {mySubmissions.length === 0 && <p className="hint">You haven't submitted any projects yet.</p>}
        <ul>
          {mySubmissions.slice(0, MY_SUBMISSIONS_PREVIEW_COUNT).map((s) => {
            const label = ((s.data?.projectName as string) || "(untitled)") + (s.data?.jobNumber ? ` — ${s.data.jobNumber}` : "");
            return (
              <li key={s.id}>
                <Link to={`/submissions/${s.id}`} className="draft-link">
                  <div className="draft-title">{label}</div>
                  <div className="draft-date">{new Date(s.submittedAt).toLocaleString()}</div>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>

      {confirmState && (
        <div
          className="delete-confirm-popover"
          role="dialog"
          aria-label="Confirm delete draft"
          style={{ position: "fixed", top: confirmState.y, left: confirmState.x, zIndex: 1000 }}
        >
          <div className="delete-confirm-text">Delete this draft?</div>
          <div className="delete-confirm-actions">
            <button type="button" className="btn-secondary" onClick={() => setConfirmState(null)}>Cancel</button>
            <button type="button" className="btn-danger" onClick={confirmDelete} autoFocus>Delete</button>
          </div>
        </div>
      )}
    </aside>
  );
}
